import { EMAIL_PROBLEM, isEmailAddress } from '@app/shared'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { Ref } from 'react'
import { useState } from 'react'
import { useTRPC } from '../lib/trpc'
import { Button, Field, Notice, Question, TextInput } from '../ui'
import { Challenge } from '../ui/Challenge'

/**
 * View my account: an address typed, and the account's private link mailed to
 * it.
 *
 * The answer on screen is one sentence whatever the address was, because the
 * address is only a claim — saying whether it was found would tell anybody
 * typing a neighbor's address that the neighbor belongs here. It says what
 * to do if nothing arrives, which is the one thing either case needs.
 */
export function AccountLinkRequest({
  orgId,
  stepRef,
  ask = 'Where should we send your account link?',
}: {
  orgId: string
  stepRef?: Ref<HTMLDivElement>
  /** The question, in the words of the path that led here. */
  ask?: string
}) {
  const trpc = useTRPC()
  const challenge = useQuery(trpc.turnstile.siteKey.queryOptions())
  const [email, setEmail] = useState('')
  const [emailLeft, setEmailLeft] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  /* A challenge token is good once, so every send spends it — a refused one
     included — and the check is offered again. */
  const [spentAt, setSpentAt] = useState(0)
  const request = useMutation({
    ...trpc.donation.accountLink.mutationOptions(),
    onSettled: () => setSpentAt(Date.now()),
  })

  return (
    <Question
      ref={stepRef}
      ask={request.isSuccess ? 'Check your inbox' : ask}
      appearance="page"
    >
      {request.isSuccess ? (
        <Notice>
          If that address is on an account here, a private link to it is on its
          way. It opens your statement, your membership and what is ongoing,
          with a way to pay. If nothing arrives in a few minutes, please contact
          us.
        </Notice>
      ) : (
        <>
          <Field
            label="Email"
            problem={
              emailLeft && email.trim() && !isEmailAddress(email)
                ? EMAIL_PROBLEM
                : null
            }
          >
            <TextInput
              type="email"
              onBlur={() => setEmailLeft(true)}
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          <Challenge
            siteKey={challenge.data?.siteKey ?? null}
            onToken={setToken}
            spentAt={spentAt}
          />
          {request.error && (
            <Notice tone="error">{request.error.message}</Notice>
          )}
          <Button
            disabled={
              !isEmailAddress(email) ||
              request.isPending ||
              (Boolean(challenge.data?.siteKey) && token === null)
            }
            onClick={() =>
              request.mutate({
                shortId: orgId,
                email: email.trim(),
                challenge: token,
              })
            }
          >
            Send my link
          </Button>
        </>
      )}
    </Question>
  )
}
